import {
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseHighchart from '../../BaseHighchart';
import {
  ChartDataType,
} from '../../types';

import { STACKED_SOIL_MOISTURE_NAME, CHART_TITLE_HUMEDAD_SUELO_APILADO } from '../../../../constants/graphs';
import apiClient from '../../../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { navigateToChart } from '../../../../utils/helpers';
import { FetchError } from '../../../../views/types';
import ChartFilter from '../../ChartFilter';
import ConversationModal from '../../ConversationTab/conversationModal';
import { calculateSeriesOffset } from '../builder';
import { getChartConfig, getStackedTooltip, getYAxis } from '../utils';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import StackedSoilMoistureConfigContent from './StackedSoilMoistureConfigContent';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

function StackedSoilMoistureChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = true,
  hideYValues = false,
  height = 600,
  title = CHART_TITLE_HUMEDAD_SUELO_APILADO,
  yAxisTitle = 'Humedad Suelo (%)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  isGeneratingPDF = false,
  refreshTrigger,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
  isGeneratingPDF?: boolean;
  refreshTrigger?: number;
}) {
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    absoluteCategories: [],
  });

  const [showOriginal, setShowOriginal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(1);
  const chartName = 'humedad-suelo-apilada';

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const transformData = useCallback(
    (
      preparedData: ChartDataType,
      shouldShowOriginal: boolean,
    ) => {
      if (!interval) return;
      const {
        series: seriesData,
        min: minY,
        max: maxY,
      } = calculateSeriesOffset(preparedData.series, shouldShowOriginal);
      setMin(minY - 1);
      setMax(maxY + 1);

      setChartData({
        absoluteCategories: preparedData.absoluteCategories,
        series: seriesData,
      });
    }, [interval],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (interval) {
        let rawChartData;
        const telemetryType = selectedSector?.telemetryType || 'wiseconn';
        try {
          const { data } = await apiClient.readings.getStackedSoilMoistureData({
            farmId,
            startDate: interval.startDate,
            endDate: interval.endDate,
            sensorType: 'Soil Moisture',
            telemetryType,
            ...(selectedSector?.sn && telemetryType === 'sn' && { sn: selectedSector.sn }),
            sectorId,
            chart: STACKED_SOIL_MOISTURE_NAME,
          });

          rawChartData = data.seriesData || { series: [], absoluteCategories: [] };
        } catch (error) {
          console.error(`Error fetching ${telemetryType} data:`, error);
          rawChartData = { series: [], absoluteCategories: [] };
        }
        transformData(rawChartData, showOriginal);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [interval, showOriginal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchData();
    }
  }, [refreshTrigger, fetchData]);

  const customTooltip = useMemo(() => ({
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      return getStackedTooltip(this, conversations);
    },
  }), [showOriginal, conversations]);

  const chartConfig = useMemo(() => interval && getChartConfig(
    chartData,
    height,
    hideXValues,
    {
      ...getYAxis(yAxisTitle, hideYValues || !showOriginal),
      min,
      max,
    } as Highcharts.YAxisOptions,
    dayjs(interval.startDate).valueOf(),
    dayjs(interval.endDate).valueOf(),
  ), [
    height,
    hideXValues,
    chartData,
    interval,
    hideYValues,
    showOriginal,
  ]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            STACKED_SOIL_MOISTURE_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  return (
    <>
      <Box>
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" height={height}>
            <CircularProgress />
          </Box>
        )}
        <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
      <BaseHighchart
        chartView={chartView}
        sectorId={sectorId}
        graphName={STACKED_SOIL_MOISTURE_NAME}
        filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
        title={title}
        adjustYWithLegend={true}
        yAxisTitle={showOriginal ? '' : yAxisTitle}
        series={chartData.series as Highcharts.SeriesOptionsType[]}
        tooltip={customTooltip}
        hideYValues={showOriginal || hideYValues}
        showFullScreen={true}
        legend={{ enabled: !hideLegend }}
        stackButton={isGeneratingPDF ? undefined : {
          onClick: () => setShowOriginal(!showOriginal),
          isStacked: showOriginal,
          component: (
            <FormControlLabel
              control={
                <Switch
                  checked={!showOriginal}
                  onChange={() => setShowOriginal(!showOriginal)}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      color: '#a8d4f7',
                      '&.Mui-checked': {
                        color: '#3693d5',
                      },
                      '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3693d5',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#a8d4f7',
                    },
                  }}
                />
              }
              label={showOriginal ? 'Original' : 'Apilado'}
              sx={{ mr: 2 }}
            />
          ),
        }}
        chartConfig={chartConfig}
        onConfigClick={handleConfigClick}
        onFullScreenClick={handleFullScreenClick}
        absoluteCategories={chartData.absoluteCategories}
        setModalState={setModalState}
        conversations={conversations}
        height={height}
        zoomSync={true}
        autoAdjustYAxis={true}
        interval={interval}
      />
      </div>
      </Box>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={STACKED_SOIL_MOISTURE_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Humedad de suelo (apilada)"
      >
        <StackedSoilMoistureConfigContent
          sectorId={sectorId}
          onConfigSaved={() => fetchData()}
        />
      </ChartConfigDialog>
    </>
  );
}

export default StackedSoilMoistureChart;
